import { FC, ReactNode, useState } from 'react'
import {
  ControlContextProvider,
  InputSelectControl,
  InputSelectControlField,
  SelectControl
} from '@sequencehq/core-components'
import { CustomPaymentTermsModal } from './CustomPaymentTermsModal'
import { inflect } from 'inflection'
import { DEFAULT_NET_PRESETS } from 'SettingsV2/index'
type ValidationResult = { message: string; metadata?: Record<string, unknown> }

type PaymentTermsSelectProps = {
  value?: number
  onChange: (value: number) => void
  disabled?: boolean
  validationErrors?: ValidationResult[]
  placeholder?: string
  triggerStyle?: React.CSSProperties
  customTerms?: number[]
  isPending?: boolean
  id?: string
  useFieldVariant?: boolean
  label?: string
  width?: string | number
  trigger?: ReactNode
  onToggle?: (isOpen: boolean) => void
  'data-testid'?: string
}

export const PaymentTermsSelect: FC<PaymentTermsSelectProps> = ({
  value,
  onChange,
  disabled = false,
  validationErrors = [],
  placeholder = 'Payment terms in days',
  triggerStyle,
  customTerms = [],
  isPending = false,
  id,
  useFieldVariant = false,
  label,
  width,
  trigger,
  onToggle,
  'data-testid': testId
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const allTerms = [
    ...DEFAULT_NET_PRESETS,
    ...customTerms.filter(term => !DEFAULT_NET_PRESETS.includes(term))
  ].sort((a, b) => a - b)

  const options = [
    ...allTerms.map(time => ({
      value: `${time}`,
      label: `${time} days`,
      secondaryLabel: `Net ${time}`
    })),
    {
      value: 'custom',
      label: 'Custom',
      secondaryLabel: 'Custom'
    }
  ]

  const getSelectedValue = () => {
    if (value == null) return undefined
    return allTerms.includes(value) ? `${value}` : 'custom'
  }

  const getOverrideLabel = () => {
    if (value == null || allTerms.includes(value)) return undefined
    return `${value} ${inflect('day', value)}`
  }

  const handleChange = (selectedValue: string) => {
    if (selectedValue === 'custom') {
      setIsModalOpen(true)
      return
    }
    const parsedValue = Number.parseInt(selectedValue, 10)
    if (!Number.isNaN(parsedValue)) {
      onChange(parsedValue)
    }
  }

  const handleModalSubmit = (customValue: number) => {
    onChange(customValue)
    setIsModalOpen(false)
  }

  const commonProps = {
    id,
    placeholder,
    disabled: disabled || isPending,
    initialValue: getSelectedValue(),
    overrideSelectedLabel: getOverrideLabel(),
    options,
    onChange: handleChange,
    triggerStyle: triggerStyle || (width ? { width } : undefined),
    justifyLabels: 'space-between',
    onToggle,
    'data-testid': testId
  }

  if (useFieldVariant) {
    return (
      <>
        <InputSelectControlField
          {...commonProps}
          label={label}
          validationErrors={validationErrors}
          width={typeof width === 'number' ? `${width}px` : width}
          matchWidth
        />
        <CustomPaymentTermsModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          isPending={isPending}
          onSubmit={handleModalSubmit}
        />
      </>
    )
  }

  return (
    <>
      {trigger ? (
        <ControlContextProvider>
          <SelectControl {...commonProps} trigger={trigger} />
        </ControlContextProvider>
      ) : (
        <InputSelectControl {...commonProps} />
      )}
      <CustomPaymentTermsModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        isPending={isPending}
        onSubmit={handleModalSubmit}
      />
    </>
  )
}
